import {dataTestId} from '@hconnect/uikit'
import {Loader} from '@hconnect/uikit/src/lib2'
import {Stack, TextField} from '@mui/material'
import React, {useEffect, useMemo} from 'react'
import {Controller, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {submitOnBlurAndEnterProps} from '../../../common/utils'
import {useDepartmentDetails} from '../../hooks/upmEntityConfig/useDepartmentDetails'
import {useUpdateDepartmentEnglishName} from '../../hooks/upmEntityConfig/useUpdateDepartmentEnglishName'

interface DepartmentEnglishNameFormProps {
  departmentUpmId: string
}

type DepartmentEnglishNameFormValues = {
  departmentEnglishName: string
}

export const DepartmentEnglishNameForm: React.FC<DepartmentEnglishNameFormProps> = ({
  departmentUpmId
}) => {
  const {t} = useTranslation()
  const {
    data: department,
    isLoading: isLoading,
    isFetching: isFetching
  } = useDepartmentDetails(departmentUpmId)
  const {mutate: updateEnglishName, isPending: isUpdating} = useUpdateDepartmentEnglishName()

  const defaultValues = useMemo<DepartmentEnglishNameFormValues>(
    () => ({
      departmentEnglishName: department?.displayValueEnglish ?? ''
    }),
    [department?.displayValueEnglish]
  )

  const {
    handleSubmit,
    control,
    reset,
    formState: {dirtyFields}
  } = useForm<DepartmentEnglishNameFormValues>({
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues
  })

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const submit = handleSubmit((values) => {
    if (!dirtyFields.departmentEnglishName) return

    department &&
      values &&
      updateEnglishName(
        {...department, displayValueEnglish: values.departmentEnglishName},
        {
          onError: () => reset(defaultValues),
          onSuccess: () => {
            reset({departmentEnglishName: values.departmentEnglishName})
          }
        }
      )
  })

  if (isLoading || isFetching) {
    return <Loader />
  }

  return (
    <Stack spacing={2} maxWidth={500} {...dataTestId('department_english_name_form')}>
      <Controller
        control={control}
        name="departmentEnglishName"
        render={({field: {ref, value, onChange}}) => (
          <TextField
            label={t('plantSetup.departmentEnglishName')}
            inputRef={ref}
            value={value}
            onChange={onChange}
            disabled={isUpdating}
            {...submitOnBlurAndEnterProps(submit)}
            {...dataTestId('department_english_name_input')}
          />
        )}
      />
    </Stack>
  )
}
